import { apiClientV2, baseV2URL, unwrapV2Response } from './index.js';
import { getToken } from '@contexts/Token.context';

/**
 * "Find similar" API: the user marks one example object on the orthophoto, and the backend
 * scores every feature patch of the orthophoto by how similar it is. All geometry is EPSG:3857.
 */

const similarityPath = projectUuid => `/projects/${projectUuid}/similarity`;

/** The backbone whose features are used unless the user picks another. */
export const DEFAULT_BACKBONE = 'bfm-v1';

/**
 * The feature backbones of the orthophoto, and whether their features are computed.
 * @param {Object} params
 * @param {string} params.projectUuid
 * @returns {Promise<Array<{key: string, label: string, state: string}>>} `state` is `ready`,
 * `missing`, `computing` or `failed`
 */
export const getSimilarityBackbones = async ({ projectUuid }) => {
	const { data, error } = await apiClientV2.get(`${similarityPath(projectUuid)}/backbones`);
	if (error) throw error;
	return unwrapV2Response(data)?.backbones ?? [];
};

/**
 * @param {Object} params
 * @param {string} params.projectUuid
 * @param {number[][]} params.boxes - [[minX, minY, maxX, maxY]] in EPSG:3857
 * @param {string} [params.backbone] - Which features to compare; an exemplar belongs to one
 * @returns {Promise<{exemplar_id: string, status: 'ready'|'features_missing',
 * features_state: string, value_range: {lo: number, hi: number}, patch_m: number}>}
 */
export const createSimilarityExemplar = async ({
	projectUuid,
	boxes,
	backbone = DEFAULT_BACKBONE,
}) => {
	const { data, error } = await apiClientV2.post(`${similarityPath(projectUuid)}/exemplars`, {
		boxes,
		backbone,
	});
	if (error) throw error;
	return unwrapV2Response(data);
};

/**
 * @param {Object} params
 * @param {string} params.projectUuid
 * @param {string} params.exemplarId
 * @param {number} params.minSimilarity
 * @returns {Promise<Object>} GeoJSON Points, best first, with `{ similarity, rank, bbox }`
 */
export const getSimilaritySuggestions = async ({
	projectUuid,
	exemplarId,
	minSimilarity,
	backbone = DEFAULT_BACKBONE,
	bounds,
}) => {
	const { data, error } = await apiClientV2.get(
		`${similarityPath(projectUuid)}/exemplars/${exemplarId}/suggestions`,
		{
			limit: 200,
			min_similarity: minSimilarity,
			backbone,
			// The best 200 in the view, not in the whole orthophoto (F01e)
			...(bounds && { bounds: bounds.join(',') }),
		}
	);
	if (error) throw error;
	return unwrapV2Response(data);
};

/**
 * One similarity tile (XYZ, 256 px): R = encoded similarity, A = 0 outside the orthophoto.
 * Fetched here because an image URL can't carry the token.
 * @returns {Promise<ImageBitmap|null>} Null outside the orthophoto
 */
export const fetchSimilarityTile = async ({
	projectUuid,
	exemplarId,
	backbone = DEFAULT_BACKBONE,
	z,
	x,
	y,
	signal,
}) => {
	const url = `${baseV2URL}${similarityPath(projectUuid)}/tiles/${z}/${x}/${y}.png?exemplar=${exemplarId}&backbone=${backbone}`;
	const response = await fetch(url, {
		headers: { Authorization: `Bearer ${getToken()}` },
		signal,
	});
	if (response.status === 204 || response.status === 404) return null;
	if (!response.ok) throw new Error(`Similarity tile ${z}/${x}/${y} failed (${response.status})`);
	// Keep the encoded values as sent: no colour management, no premultiplied alpha
	return createImageBitmap(await response.blob(), {
		colorSpaceConversion: 'none',
		premultiplyAlpha: 'none',
	});
};

/**
 * The backbone feature grid of the orthophoto, for the hover preview.
 * @param {Object} params
 * @param {string} params.projectUuid
 * @returns {Promise<{store_id: string, origin: number[], patch_units: number, patch_m: number,
 * grid: number[], k: number, scale: number, tile_patches: number, tiles: number[]}>} `origin` is
 * the top-left corner in EPSG:3857; `grid` is [rows, cols]
 */
export const getSimilarityFeatureGrid = async ({ projectUuid, backbone = DEFAULT_BACKBONE }) => {
	const { data, error } = await apiClientV2.get(`${similarityPath(projectUuid)}/features/grid`, {
		backbone,
	});
	if (error) throw error;
	return unwrapV2Response(data);
};

/**
 * One tile of patch features: 128 × 128 patches × (k + 1) bytes, row-major. Per patch, k feature
 * bytes ((byte − 128) × scale) and a data byte (255 on the orthophoto, 0 off it).
 * @returns {Promise<Uint8Array|null>} Null for a tile with no data
 */
export const fetchSimilarityFeatureTile = async ({
	projectUuid,
	backbone = DEFAULT_BACKBONE,
	store,
	k,
	tx,
	ty,
	signal,
}) => {
	const url = `${baseV2URL}${similarityPath(projectUuid)}/features/tiles/${tx}/${ty}.bin?store=${store}&k=${k}&backbone=${backbone}`;
	const response = await fetch(url, {
		headers: { Authorization: `Bearer ${getToken()}` },
		signal,
	});
	if (response.status === 204) return null;
	if (!response.ok) throw new Error(`Feature tile ${tx}/${ty} failed (${response.status})`);
	return new Uint8Array(await response.arrayBuffer());
};
